import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, IconButton, TextField, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { PLAN_COLORS } from '../constants';
import type { WarmupStep } from '../types';

const DEBOUNCE_MS = 400;

interface WarmupScheduleEditorProps {
  steps: WarmupStep[];
  onChange: (steps: WarmupStep[]) => void;
  disabled?: boolean;
}

interface WarmupStepRowProps {
  step: WarmupStep;
  index: number;
  barWidth: number;
  canDelete: boolean;
  disabled?: boolean;
  onPatch: (index: number, patch: Partial<WarmupStep>) => void;
  onDelete: (index: number) => void;
  onCommit: () => void;
}

const WarmupStepRow = memo(function WarmupStepRow({
  step,
  index,
  barWidth,
  canDelete,
  disabled,
  onPatch,
  onDelete,
  onCommit,
}: WarmupStepRowProps) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr 24px',
        gap: 0.5,
        alignItems: 'center',
        mb: 0.5,
      }}
    >
      <TextField
        type="number"
        size="small"
        disabled={disabled}
        value={step.dayFrom}
        onChange={(e) => onPatch(index, { dayFrom: Math.max(1, parseInt(e.target.value, 10) || 1) })}
        onBlur={onCommit}
        inputProps={{ min: 1, 'aria-label': `Warm-up from day ${index + 1}` }}
        sx={{ '& .MuiOutlinedInput-input': { py: 0.5, fontSize: 12, fontFamily: 'monospace' } }}
      />
      <TextField
        type="number"
        size="small"
        disabled={disabled}
        value={step.dayTo}
        onChange={(e) => onPatch(index, { dayTo: Math.max(1, parseInt(e.target.value, 10) || 1) })}
        onBlur={onCommit}
        inputProps={{ min: 1, 'aria-label': `Warm-up to day ${index + 1}` }}
        sx={{ '& .MuiOutlinedInput-input': { py: 0.5, fontSize: 12, fontFamily: 'monospace' } }}
      />
      <Box>
        <TextField
          type="number"
          size="small"
          disabled={disabled}
          value={step.emails}
          onChange={(e) =>
            onPatch(index, { emails: Math.max(1, parseInt(e.target.value, 10) || 1) })
          }
          onBlur={onCommit}
          inputProps={{ min: 1, 'aria-label': `Emails day ${index + 1}` }}
          sx={{
            '& .MuiOutlinedInput-input': {
              py: 0.5,
              fontSize: 12,
              fontFamily: 'monospace',
              color: PLAN_COLORS.orange,
            },
            '& .MuiOutlinedInput-root fieldset': { borderColor: PLAN_COLORS.orangeBg },
          }}
        />
        <Box
          sx={{
            height: 4,
            bgcolor: PLAN_COLORS.orange,
            borderRadius: 0.5,
            width: `${barWidth}%`,
            mt: 0.25,
          }}
        />
      </Box>
      {canDelete && !disabled ? (
        <IconButton
          size="small"
          onClick={() => onDelete(index)}
          sx={{ p: 0.25, color: 'text.secondary' }}
          aria-label="Delete warm-up step"
        >
          <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
      ) : (
        <span />
      )}
    </Box>
  );
});

export const WarmupScheduleEditor = ({ steps, onChange, disabled }: WarmupScheduleEditorProps) => {
  const [draft, setDraft] = useState(steps);
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Sync when parent replaces steps (load version, reset from outside).
  useEffect(() => {
    setDraft(steps);
  }, [steps]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const commitNow = useCallback((next: WarmupStep[]) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    draftRef.current = next;
    setDraft(next);
    onChangeRef.current(next);
  }, []);

  const scheduleCommit = useCallback((next: WarmupStep[]) => {
    draftRef.current = next;
    setDraft(next);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      onChangeRef.current(next);
    }, DEBOUNCE_MS);
  }, []);

  const flushPending = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
      onChangeRef.current(draftRef.current);
    }
  }, []);

  const updateStep = useCallback((index: number, patch: Partial<WarmupStep>) => {
    const next = draftRef.current.map((s, i) => (i === index ? { ...s, ...patch } : s));
    scheduleCommit(next);
  }, [scheduleCommit]);

  const addStep = () => {
    const last = draft[draft.length - 1];
    const next = [
      ...draft,
      {
        dayFrom: last.dayTo + 1,
        dayTo: last.dayTo + 15,
        emails: Math.min(last.emails + 100, 2000),
      },
    ];
    setDraft(next);
    commitNow(next);
  };

  const deleteStep = (index: number) => {
    if (draft.length < 2) return;
    const next = draft.filter((_, i) => i !== index);
    setDraft(next);
    commitNow(next);
  };

  const reset = () => {
    const next = steps.map((s) => ({ ...s }));
    setDraft(next);
    commitNow(next);
  };

  const maxEmails = Math.max(...draft.map((s) => s.emails), 1);
  const ramp = draft[draft.length - 1]?.dayTo ?? 0;
  const cap = draft[draft.length - 1]?.emails ?? 0;

  return (
    <Box>
      <Typography sx={{ fontSize: 11, color: 'text.secondary', mb: 1, lineHeight: 1.5 }}>
        Daily send limit for each new sending address. Each address follows this ramp from day 1
        of its life.
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 24px', gap: 0.5, mb: 0.5 }}>
        <Typography sx={{ fontSize: 10, color: 'text.secondary', fontFamily: 'monospace' }}>
          From
        </Typography>
        <Typography sx={{ fontSize: 10, color: 'text.secondary', fontFamily: 'monospace' }}>
          To
        </Typography>
        <Typography sx={{ fontSize: 10, color: 'text.secondary', fontFamily: 'monospace' }}>
          Emails/day
        </Typography>
        <span />
      </Box>
      {draft.map((step, i) => (
        <WarmupStepRow
          key={i}
          step={step}
          index={i}
          barWidth={Math.round((step.emails / maxEmails) * 100)}
          canDelete={draft.length > 1}
          disabled={disabled}
          onPatch={updateStep}
          onDelete={deleteStep}
          onCommit={flushPending}
        />
      ))}
      <Box sx={{ display: 'flex', gap: 0.75, mt: 1 }}>
        <Button
          variant="outlined"
          size="small"
          onClick={addStep}
          disabled={disabled}
          sx={{ flex: 1, fontSize: 11, py: 0.5, borderStyle: 'dashed' }}
        >
          + Add step
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={reset}
          disabled={disabled}
          sx={{ flex: 1, fontSize: 11, py: 0.5 }}
        >
          Reset
        </Button>
      </Box>
      <Typography
        sx={{
          mt: 1,
          fontSize: 10,
          color: 'text.secondary',
          fontFamily: 'monospace',
          px: 1,
          py: 0.625,
          bgcolor: 'action.hover',
          borderRadius: 1,
        }}
      >
        {draft.length} steps · ramp ends day {ramp} · max {cap} emails/day
      </Typography>
    </Box>
  );
};
